// Initial changes from ADG4CustomerTestimonials
// Removed scroll Ref and inview tracker, since there is no longer an automatic transition to new tabs
// Removed className since it was not in use
// Removed testimonialCta since that is a non standard usage in the Confluence Product Tour and clouds the core Design Pattern
// Removed Blue as an option for backgrounds for now since that is not spec
// Removed h2 heading (that should be included as a Content Block separately going forward)
// Removed TypographyStyles wrapper for consistency with other components/patterns
// Cleaned up unused classnames within the component

import { css, cssMap } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs,
    Adg4TabPanel
} from "@/components/adg4/tabs/src";
import {
    ContentPlatformImage,
    ContentPlatformImageProps
} from "@/components/content-platform-image/src/content-platform-image";
import { Picture } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { LinkType } from "@/libraries/design/components/link/src/link";
import {
    DataEventProps,
    ProductKeyTypes,
    Row
} from "@/libraries/wpl/foundations/src";

type BgColorType = "black" | "white";

export interface CustomerTestimonialsProps {
    bgColor?: BgColorType;
    testimonials: TestimonialProps[];
    productKey?: ProductKeyTypes;
}

export type TestimonialProps = {
    eyebrow?: string;
    logo: JSX.Element | ContentPlatformImageProps;
    name: string;
    title: string;
    image: string | ContentPlatformImageProps;
    quote: string;
    link?: {
        text: string;
        url: string;
        type?: LinkType;
        dataEventProps?: DataEventProps;
    };
    label?: string;
};

// Not able to use wacToken function with cssMap. Need to refactor this in the future to use dynamic functions
const backgroundColorStyleMap = cssMap({
    // eslint-disable-next-line local/ensure-wac-token-usage
    black: { background: "#000" },
    // eslint-disable-next-line local/ensure-wac-token-usage
    white: { background: "#fff" }
});

const backgroundShape = css({
    clipPath: "polygon(0 10%, 100% 0, 100% 100%, 0% 100%)",
    [media.above.sm]: {
        clipPath: "polygon(2% 16%, 100% 0, 100% 100%, 0% 100%)"
    }
});

const fontColorStyleMap = cssMap({
    // eslint-disable-next-line local/ensure-wac-token-usage
    "dark-bg": { color: "#fff !important" },
    "white-bg": { color: "unset" }
});

const topLineStyleMap = cssMap({
    // eslint-disable-next-line local/ensure-wac-token-usage
    black: { backgroundColor: "#fff" },
    // eslint-disable-next-line local/ensure-wac-token-usage
    white: { backgroundColor: "#000" }
});

const logoOptionStyleMap = cssMap({
    "dark-bg": {
        svg: {
            path: {
                // eslint-disable-next-line local/ensure-wac-token-usage
                fill: "#fff"
            }
        },
        selectors: {
            "&[aria-selected=true]": {
                ".top-marker": {
                    // eslint-disable-next-line local/ensure-wac-token-usage
                    backgroundColor: "#fff"
                }
            },
            ".tab-logo": {
                filter: "invert(1)"
            }
        }
    },
    "white-bg": {
        svg: {
            path: {
                // eslint-disable-next-line local/ensure-wac-token-usage
                fill: "#000"
            }
        },
        selectors: {
            "&[aria-selected=true]": {
                ".top-marker": {
                    // eslint-disable-next-line local/ensure-wac-token-usage
                    backgroundColor: "#000"
                }
            }
        }
    }
});

const justifyContentStyleMap = cssMap({
    "dark-bg": { justifyContent: "end" },
    "white-bg": { justifyContent: "center" }
});

export const CustomerTestimonials = ({
    bgColor = "black",
    testimonials,
    productKey
}: CustomerTestimonialsProps) => {
    const bgTheme = bgColor === "black" ? "dark-bg" : "white-bg";

    return (
        <Adg4Tabs loop={true}>
            <section>
                <div
                    css={[
                        testimonialKeyframeStyles,
                        testimonialStyles,
                        fontColorStyleMap[bgTheme]
                    ]}
                >
                    <Row>
                        <div>
                            <div
                                css={[
                                    bgShapeStyle,
                                    backgroundColorStyleMap[bgColor],
                                    bgColor === "black" && backgroundShape
                                ]}
                            />
                            <div css={tabPanelWrapperStyles}>
                                {testimonials.map((testimonial, index) => {
                                    return (
                                        <Adg4TabPanel
                                            id={`${index}`}
                                            panelIndex={index}
                                            key={index}
                                            forceRender={true}
                                            hideTabPanelRole={true}
                                        >
                                            <TopRowContent
                                                testimonial={testimonial}
                                                index={index}
                                                bgTheme={bgTheme}
                                                bgColor={bgColor}
                                                productKey={productKey}
                                            />
                                        </Adg4TabPanel>
                                    );
                                })}
                            </div>

                            {testimonials.length > 1 && (
                                <div css={rowWidthStyle}>
                                    <div css={logosContainerBaseStyle}>
                                        <Adg4DraggableTabList
                                            css={logosStyle}
                                            rowGap="32px"
                                        >
                                            <div
                                                css={[
                                                    topLineStyles,
                                                    topLineStyleMap[bgColor]
                                                ]}
                                            />
                                            {testimonials.map(
                                                (testimonial, index) => {
                                                    return (
                                                        <Adg4DraggableTab
                                                            as="li"
                                                            css={[
                                                                logoOptionStyleMap[
                                                                    bgTheme
                                                                ],
                                                                logoOptionStyle
                                                            ]}
                                                            key={index}
                                                            tabIndex={index}
                                                            dataEventProps={{
                                                                "data-product-key":
                                                                    productKey,
                                                                "data-event-component":
                                                                    "tab",
                                                                "data-event-container":
                                                                    "testimonialsSection",
                                                                "data-event":
                                                                    "clicked",
                                                                "data-label-english":
                                                                    testimonial.label
                                                            }}
                                                            label={
                                                                testimonial.label
                                                            }
                                                        >
                                                            <LogoTabContent
                                                                testimonialLogo={
                                                                    testimonial.logo
                                                                }
                                                            />
                                                        </Adg4DraggableTab>
                                                    );
                                                }
                                            )}
                                        </Adg4DraggableTabList>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Row>
                </div>
            </section>
        </Adg4Tabs>
    );
};

interface TopRowContentInterface {
    testimonial: TestimonialProps;
    index: number;
    bgTheme: "dark-bg" | "white-bg";
    bgColor: BgColorType;
    isPanelActive?: boolean;
    productKey?: ProductKeyTypes;
}

const TopRowContent = ({
    testimonial,
    index,
    bgTheme,
    bgColor,
    isPanelActive,
    productKey
}: TopRowContentInterface) => {
    return (
        <div
            css={[
                rowWidthStyle,
                topRowStyle,
                isPanelActive && topRowActiveStyle
            ]}
        >
            <div css={[customerImagesSectionStyles]}>
                <div
                    css={[
                        customerImagesSectionImageStyles,
                        isPanelActive && customerImagesImgActiveStyle
                    ]}
                >
                    {typeof testimonial.image !== "string" &&
                    "contentType" in testimonial.image ? (
                        <ContentPlatformImage {...testimonial.image} />
                    ) : (
                        <Picture
                            image={{
                                src: testimonial.image as string,
                                alt: ""
                            }}
                        />
                    )}
                </div>
            </div>
            <div css={[customerQuotesSectionStyle]}>
                <div
                    css={[
                        testimonialQuotesSectionStyle,
                        justifyContentStyleMap[bgTheme]
                    ]}
                >
                    <div
                        css={[
                            quoteContainerStyle,
                            isPanelActive && quoteContainerActiveStyle
                        ]}
                        key={index}
                    >
                        {testimonial.eyebrow && (
                            <p
                                css={[
                                    fontColorStyleMap[bgTheme],
                                    quoteContainerPStyle
                                ]}
                                className="eyebrow-md"
                            >
                                {testimonial.eyebrow}
                            </p>
                        )}
                        <blockquote
                            css={[
                                fontColorStyleMap[bgTheme],
                                quoteContainerPStyle
                            ]}
                            className="callout"
                        >
                            “{testimonial.quote}”
                        </blockquote>
                        <p css={quoteContainerPStyle}>
                            <span
                                css={fontColorStyleMap[bgTheme]}
                                className="eyebrow-lg"
                            >
                                {testimonial.name}
                            </span>
                            <br />
                            <span
                                css={fontColorStyleMap[bgTheme]}
                                className="body-md title"
                            >
                                {testimonial.title}
                            </span>
                        </p>
                        {testimonial.link ? (
                            <Link
                                anchorProps={{
                                    tabIndex: isPanelActive ? 0 : -1
                                }}
                                text={testimonial.link.text}
                                href={testimonial.link.url}
                                type={testimonial.link.type ?? "primary"}
                                bgColor={
                                    bgColor === "black"
                                        ? "bgBlackBlue"
                                        : "bgWhite"
                                }
                                aria-label={testimonial.link.text}
                                dataEventProps={
                                    testimonial.link.dataEventProps ?? {
                                        "data-product-key": productKey,
                                        "data-event-component": "linkButton",
                                        "data-event-container":
                                            "testimonialsSection",
                                        "data-event": "clicked"
                                    }
                                }
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

const topRowActiveStyle = css({
    opacity: 1,
    zIndex: 2
});
const customerImagesImgActiveStyle = css({
    // Picture component does not accept compiled CSS. Using nested selector to apply CSS to displayed image.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        animation: "fadein 1s ease-out 1 forwards"
    }
});

const quoteContainerActiveStyle = css({
    animation: "fadeswipe 1s 1",

    [media.below.sm]: {
        animation: "fadeswipesmall 1s 1"
    }
});

interface LogoTabContentProps {
    testimonialLogo: JSX.Element | ContentPlatformImageProps;
    isSelected?: boolean;
}

const LogoTabContent = ({
    testimonialLogo,
    isSelected = false
}: LogoTabContentProps) => {
    const isContentfulImage = "contentType" in testimonialLogo;

    return (
        <>
            <span css={topMarkerStyle} className="top-marker" />
            <span css={[logoButtonStyle]}>
                <span
                    css={[
                        logoImgStyle,
                        logoImgContainerStyle,
                        isSelected && logoImgSelectedStyle
                    ]}
                >
                    {isContentfulImage ? (
                        <ContentPlatformImage
                            {...testimonialLogo}
                            width="140"
                            height="34"
                            style={{
                                objectFit: "contain",
                                objectPosition: "center center"
                            }}
                            className="tab-logo"
                        />
                    ) : (
                        (testimonialLogo as JSX.Element)
                    )}
                </span>
            </span>
        </>
    );
};

// LogoTabContent styles

const logoImgStyle = css({
    "opacity": 0.5,
    "transition": "0.5s",
    ":hover": {
        opacity: 1
    }
});

const logoImgSelectedStyle = css({
    opacity: "1"
});

const logoButtonStyle = css({
    background: "none",
    border: 0,
    appearance: "none",
    paddingBlockStart: "20px",
    cursor: "pointer",
    position: "relative"
});

const topMarkerStyle = css({
    opacity: "1",
    position: "absolute",
    insetBlockStart: "-3px",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    height: "5px",
    borderRadius: "2px"
});

const logoImgContainerStyle = css({
    height: "34px",
    display: "flex",
    alignItems: "center"
});

// ADG4CustomerTestimonials styles

const topLineStyles = css({
    position: "absolute",
    insetBlockStart: "1px",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    height: "1px",
    borderRadius: "2px",
    opacity: 0.5,

    [media.below.sm]: {
        insetBlockStart: "21px"
    }
});

const testimonialKeyframeStyles = css({
    "@keyframes fadeswipe": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0,
            transform: "translateX(100px)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },
    "@keyframes fadeswipesmall": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0,
            transform: "translateX(20px)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },

    "@keyframes fadein": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1
        }
    }
});

const rowWidthStyle = css({
    position: "relative",
    display: "flex",
    // original supposed to be 250, but because paddingInlineStart was introduced to make the link
    // outline visible is we need to subtract that from the gap
    rowGap: token("space.200"),
    flexWrap: "wrap",
    alignItems: "stretch",

    [media.below.sm]: {
        flexDirection: "column",
        textAlign: "center"
    }
});

const topRowStyle = css({
    marginBlockEnd: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "32px",
    opacity: 0,
    zIndex: 1,

    [media.below.md]: {
        gridTemplateColumns: "1fr"
    }
});

const bgShapeStyle = css({
    position: "absolute",
    inset: 0,
    width: "95%",
    maxWidth: "1368px",
    margin: "auto",

    [media.below.sm]: {
        width: "100%"
    }
});

const customerImagesSectionStyles = css({
    position: "relative",
    flexBasis: "40%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",

    [media.only.sm]: {
        flexBasis: "50%"
    },

    [media.below.sm]: {
        width: "90%",
        margin: "0 auto"
    },
    [media.only.xs]: {
        width: "70%",
        margin: "0 auto"
    }
});

const customerImagesSectionImageStyles = css({
    // Picture component does not accept compiled CSS. Using nested selector to apply CSS to displayed image.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        height: "365px",

        [media.only.sm]: {
            height: "258px"
        },

        [media.only.xs]: {
            height: "258px"
        },

        [media.only.xxs]: {
            height: "210px"
        },

        width: "auto",
        display: "block"
    }
});

const customerQuotesSectionStyle = css({
    flexBasis: "60%",
    overflow: "hidden",
    // this ensures that the link outline is fully visible and isn't cut off
    paddingInlineStart: token("space.050"),
    paddingBlockEnd: token("space.050"),

    [media.below.md]: {
        flexBasis: "50%"
    }
});

const testimonialQuotesSectionStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    borderRadius: "20px",
    position: "relative",
    margin: "0 auto",

    [media.above.md]: {
        paddingBlockStart: "80px"
    },

    [media.only.sm]: {
        width: "66%"
    },

    [media.below.sm]: {
        width: "100%"
    }
});

const quoteContainerStyle = css({
    transition: "opacity 1s",
    display: "flex",
    flexDirection: "column",
    gap: "24px"
});

const quoteContainerPStyle = css({
    margin: "0"
});

const logosContainerBaseStyle = css({
    "overflow": "hidden",
    "width": "100%",
    "justifyContent": "space-between",

    "@media (max-width: 1140px)": {
        flexDirection: "column"
    },

    [media.below.sm]: {
        marginBlockStart: "20px"
    }
});

const logosStyle = css({
    // !important used to override Adg4DraggableTabList styles
    marginBlockEnd: "40px !important",
    paddingBlockStart: "1px !important",
    listStyle: "none !important"
});

const logoOptionStyle = css({
    "display": "flex",
    "justifyContent": "stretch",
    "alignItems": "stretch",
    "position": "relative",

    "&[aria-selected=true]": {
        paddingBlockStart: "0"
    }
});

const testimonialStyles = css({
    position: "relative",
    zIndex: 1,
    marginBlockEnd: token("space.500")
});

const tabPanelWrapperStyles = css({
    "display": "grid",
    "gridTemplateColumns": "1fr",
    "gridTemplateRows": "1fr",

    // active panel style overrides nested here in order to expand
    // the container to the height of largest child.
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-panel": {
        "opacity": 0,
        "zIndex": 1,
        "display": "flex",
        "gridArea": "1 / 1",

        "&.active-panel": {
            opacity: 1,
            zIndex: 2
        }
    }
});
